import { useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog.tsx';
import { Button } from './ui/button.tsx';
import { MetricsChart, type MetricsYDomainMode } from './MetricsChart.tsx';
import { type MetricTimeRange, useMetricHistory } from '../hooks/useMetricHistory.ts';
import { rollingWindowSum } from '../lib/rollingWindow.ts';

type Granularity = 'hourly' | 'daily' | 'weekly';

interface MetricHistoryModalProps {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  metricId: string;
  title: string;
  granularity: Granularity;
  yDomainMode?: MetricsYDomainMode;
  /**
   * Chart the trailing N-day SUM of the daily series instead of the raw
   * dailies. Used when the reference lines are on a monthly basis (e.g. the
   * Azure spend baseline/target in $/mo) — a monthly line must never be drawn
   * onto a daily axis. Extra history is fetched so the first displayed point
   * already has a full window behind it.
   */
  rollingSumDays?: number;
  /** Reference values drawn as dashed lines (same basis as the series). */
  baseline?: number;
  target?: number;
  /** Forwarded to the chart tooltip (e.g. currency formatting). */
  valueFormatter?: (value: number) => string;
}

const RANGES_BY_GRANULARITY: Record<Granularity, MetricTimeRange[]> = {
  hourly: ['24h', '7d', '30d', '90d'],
  daily: ['7d', '30d', '90d'],
  weekly: ['30d', '90d'],
};

export function MetricHistoryModal({
  open,
  onOpenChange,
  metricId,
  title,
  granularity,
  yDomainMode,
  rollingSumDays,
  baseline,
  target,
  valueFormatter,
}: MetricHistoryModalProps) {
  const ranges = RANGES_BY_GRANULARITY[granularity];
  const [selected, setSelected] = useState<MetricTimeRange>('90d');
  const effective: MetricTimeRange = ranges.includes(selected)
    ? selected
    : ranges[ranges.length - 1];

  const { data, isLoading, error } = useMetricHistory({
    metricId,
    timeRange: effective,
    enabled: open,
    extraDays: rollingSumDays ?? 0,
  });

  const chartPoints = useMemo(() => {
    const points = data?.dataPoints ?? [];
    // rangeStart tells the transform the fetch covered the whole window, so a
    // metric younger than the window still charts (partial totals, matching
    // the card headline) instead of drawing nothing until it is 30 days old.
    return rollingSumDays ? rollingWindowSum(points, rollingSumDays, data?.rangeStart) : points;
  }, [data, rollingSumDays]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-w-5xl'>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <div className='flex gap-2 mb-4'>
          {ranges.map((r) => (
            <Button
              key={r}
              size='sm'
              variant={r === effective ? 'default' : 'outline'}
              aria-pressed={r === effective}
              onClick={() => setSelected(r)}
            >
              {r}
            </Button>
          ))}
        </div>
        <MetricsChart
          dataPoints={chartPoints}
          isLoading={isLoading}
          error={error}
          height={400}
          yDomainMode={yDomainMode}
          baseline={baseline}
          target={target}
          valueFormatter={valueFormatter}
        />
      </DialogContent>
    </Dialog>
  );
}
